import { Monitor } from "lucide-react";
import { useState } from "react";
import type { AppUser } from "../../../shared/db.gen.ts";
import { ago, options, ROLE_LABELS } from "../lib/format.ts";
import { can, mutate, mutateWithResult } from "../lib/mutate.ts";
import { setSearch } from "../lib/router.tsx";
import { me } from "../lib/session.ts";
import { useRow, useRows } from "../lib/sync.ts";
import { CreateDialog } from "../ui/CreateDialog.tsx";
import { Badge, Button } from "../ui/controls.tsx";
import type { Column } from "../ui/DataTable.tsx";
import { ListPage } from "../ui/ListPage.tsx";
import { type FieldDef, RecordPanel } from "../ui/RecordPanel.tsx";
import { toast } from "../ui/toast.tsx";

const roleTone = { admin: "accent", inventory: "ok", receiving: "neutral" } as const;

const columns: Column<AppUser>[] = [
  {
    key: "name",
    header: "Name",
    width: "minmax(200px,1.5fr)",
    value: (u) => u.name || u.email,
    cell: (u) => (
      <span className="flex items-center gap-2">
        {u.avatar_url ? (
          <img src={u.avatar_url} alt="" className="size-5 rounded-full" referrerPolicy="no-referrer" />
        ) : (
          <span className="size-5 rounded-full bg-surface-2" />
        )}
        <span className={u.active ? "font-medium" : "text-faint line-through"}>{u.name || "—"}</span>
      </span>
    ),
  },
  {
    key: "email",
    header: "Email",
    width: "minmax(220px,1.5fr)",
    value: (u) => (u.kind === "workstation" ? "Workstation" : u.email),
    cell: (u) =>
      u.kind === "workstation" ? (
        <span className="flex items-center gap-1.5 text-muted">
          <Monitor className="size-3.5" /> Workstation · signs in with a code
        </span>
      ) : (
        u.email
      ),
  },
  { key: "role", header: "Role", width: "120px", value: (u) => ROLE_LABELS[u.role], cell: (u) => <Badge tone={roleTone[u.role]}>{ROLE_LABELS[u.role]}</Badge> },
  {
    key: "can_pay",
    header: "Can pay",
    width: "90px",
    headerTitle: "May create payments: Wise transfers, payments made outside, Wise history links (admins change it)",
    value: (u) => (u.can_pay ? "Yes" : ""),
    cell: (u) => (u.can_pay ? <Badge tone="ok">Can pay</Badge> : null),
  },
  {
    key: "last_login_at",
    header: "Last sign-in",
    width: "140px",
    cell: (u) => <span className="text-muted">{u.last_login_at ? ago(u.last_login_at) : "Never"}</span>,
  },
];

const fields: FieldDef[] = [
  { key: "name", label: "Name" },
  { key: "role", label: "Role", type: "select", options: options(ROLE_LABELS) },
  { key: "active", label: "Can sign in", type: "bool" },
  // Bills and payments --- its own permission (everyone in production is an admin): Wise transfers, payments made outside.
  // Only someone who can pay changes it, never their own (server/src/mutations/users.ts): read only otherwise.
  { key: "can_pay", label: "Can pay", type: "bool" },
];
const fieldsFor = (mayChangeCanPay: boolean) => (mayChangeCanPay ? fields : fields.map((f) => (f.key === "can_pay" ? { ...f, readOnly: true } : f)));

export function UsersPage() {
  const users = useRows("app_user");
  const [creating, setCreating] = useState(false);
  const [workstation, setWorkstation] = useState(false);
  return (
    <>
      <ListPage
        title="Users"
        rows={users}
        columns={columns}
        newLabel="Add user"
        onNew={can("user.create") ? () => setCreating(true) : undefined}
        pane="user"
        toolbar={
          can("user.create_workstation") && (
            <Button
              size="sm"
              icon={<Monitor className="size-3.5" />}
              onClick={() => setWorkstation(true)}
              title="A shared account for a PC, for people without a Google account"
            >
              Add workstation
            </Button>
          )
        }
      />
      {workstation && (
        <CreateDialog
          title="Add a workstation"
          fields={[{ key: "name", label: "Name", placeholder: "Shipping workstation" }]}
          initial={{ name: "Shipping workstation" }}
          onClose={() => setWorkstation(false)}
          onSubmit={async (v) => {
            const id = crypto.randomUUID();
            const { ok } = await mutateWithResult("user.create_workstation", { id, name: v.name || "Shipping workstation" });
            if (ok) setSearch({ open: id });
            return ok;
          }}
        />
      )}
      {creating && (
        <CreateDialog
          title="Add user"
          fields={[
            { key: "email", label: "Google account email", placeholder: "name@handheldlegend.com" },
            { key: "name", label: "Name" },
            { key: "role", label: "Role", type: "select", options: options(ROLE_LABELS) },
          ]}
          initial={{ role: "inventory" }}
          onClose={() => setCreating(false)}
          onSubmit={async (v) => {
            const id = crypto.randomUUID();
            const ok = await mutate("user.create", { id, email: v.email, name: v.name, role: v.role as AppUser["role"] });
            if (ok) setSearch({ open: id });
            return ok;
          }}
        />
      )}
    </>
  );
}

export function UserPanel({ id, close }: { id: string; close: () => void }) {
  const user = useRow("app_user", id);
  const myId = me()?.id ?? "";
  const mine = useRow("app_user", myId);
  if (!user) return null;
  const mayChangeCanPay = !!mine?.can_pay && user.id !== myId && user.role !== "receiving";
  if (user.kind === "workstation") return <WorkstationPanel user={user} close={close} />;
  return (
    <RecordPanel
      title={user.name || user.email}
      subtitle={user.email}
      badges={
        <>
          <Badge tone={roleTone[user.role]}>{ROLE_LABELS[user.role]}</Badge>
          {!user.active && <Badge tone="warn">Can't sign in</Badge>}
          {user.can_pay && <Badge tone="ok">Can pay</Badge>}
        </>
      }
      row={user}
      fields={fieldsFor(mayChangeCanPay)}
      update="user.update"
      entity="app_user"
      onClose={close}
    >
      <p className="px-2 pt-3 text-[12px] text-muted">
        People sign in with this Google account. Admins manage users and everything else; Inventory runs purchasing, receiving and payments; Receiving only uses
        the receiving screen. Can pay is its own permission: creating Wise transfers, recording payments made outside and confirming Wise history. Only someone
        who can pay already gives or takes it, and never their own.
      </p>
    </RecordPanel>
  );
}

/**
 * Workstations (migration 072; Mitch: an account on the shippers' PC, "non-admin", for people without Google
 * accounts): always Receiving, never Can pay. A PC signs in with a one-time code from here and stays signed in a year.
 */
function WorkstationPanel({ user, close }: { user: AppUser; close: () => void }) {
  const [code, setCode] = useState<{ code: string; expires_at: string } | null>(null);
  const admin = can("user.workstation_code");
  const getCode = async () => {
    const { ok, result } = await mutateWithResult("user.workstation_code", { id: user.id });
    if (ok) setCode(result as { code: string; expires_at: string });
  };
  const signOutAll = async () => {
    if (!confirm(`Sign every computer signed in as ${user.name} out? Each needs a new code to sign back in.`)) return;
    const { ok, result } = await mutateWithResult("user.sign_out_everywhere", { id: user.id });
    if (ok) {
      const n = (result as { signed_out?: number } | undefined)?.signed_out ?? 0;
      toast.ok(n ? `Signed ${n} ${n === 1 ? "computer" : "computers"} out (within 15 seconds)` : "No computer was signed in as it");
    }
  };
  return (
    <RecordPanel
      title={user.name}
      subtitle="Workstation · signs in with a code"
      badges={
        <>
          <Badge tone="neutral">{ROLE_LABELS[user.role]}</Badge>
          {!user.active && <Badge tone="warn">Can't sign in</Badge>}
        </>
      }
      row={user}
      fields={[
        { key: "name", label: "Name" },
        { key: "active", label: "Can sign in", type: "bool" },
      ]}
      update="user.update"
      entity="app_user"
      onClose={close}
    >
      <div className="flex flex-col gap-2 px-2 pt-3">
        <p className="text-[12px] text-muted">
          A shared account for a PC, for people without a Google account: the receiving screen and stock counts, nothing about money. Everything it does is
          recorded as {user.name}.
        </p>
        {admin && (
          <div className="rounded-lg border border-line p-3">
            <div className="font-medium">Sign a computer in</div>
            {code ? (
              <>
                <div className="my-2 text-center font-mono text-[26px] font-semibold tracking-[0.2em]">{code.code}</div>
                <p className="text-[12px] text-muted">
                  On the workstation, open {location.host}, click <span className="text-text">Set up this computer as a workstation</span> and type this code.
                  It works once, until {new Date(code.expires_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}; that computer then stays
                  signed in for a year.
                </p>
              </>
            ) : (
              <p className="my-1.5 text-[12px] text-muted">Get a one-time code, then type it on the workstation's sign-in page. No Google account needed.</p>
            )}
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant={code ? "ghost" : "primary"} disabled={!user.active} onClick={getCode}>
                {code ? "Get another code" : "Get a sign-in code"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="ml-auto"
                onClick={signOutAll}
                title="A lost or replaced PC: every computer signed in as it is signed out"
              >
                Sign out its computers
              </Button>
            </div>
          </div>
        )}
      </div>
    </RecordPanel>
  );
}
